import DifferenceIcon from '@mui/icons-material/Difference';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Box, Stack } from '@mui/system';
import { DiffResult, Viewer } from 'json-diff-kit';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { isApplicationPmi } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { setServiceLocale, toggleLocaleEditMode } from '@lib/core/service/slices';
import { useDevtoolsUser } from '@lib/core/users/hooks';
import { SnackbarAlert } from '@lib/tools/devtools/components/feedback/SnackBar';
import StyledTooltip from '@lib/tools/devtools/components/feedback/Tooltip';
import { Button } from '@lib/tools/devtools/components/inputs/Button';
import { DropdownSelect } from '@lib/tools/devtools/components/inputs/Select';
import { ILocaleTableDataRow } from '@lib/tools/devtools/components/interfaces';
import { TableComponent } from '@lib/tools/devtools/components/table/index';
import { themeDevTools } from '@lib/tools/devtools/components/theme';
import { useDevtoolsLocale } from '@lib/tools/locale/hooks/devtoolsLocale';
import { LocaleUtils } from '@lib/tools/locale/utils';
import { languages } from '@lib/tools/locale/utils/consts';

import 'json-diff-kit/dist/viewer.css';

export const LocalePanel = () => {
  const dispatch = useDispatch();

  const { locale, isLocaleEditMode } = useApp();
  const { isDevtoolsUserEditor } = useDevtoolsUser();
  const { detectIsDraftTermsUpdated, isDraftTermsUpdated } = useDevtoolsLocale();

  type IFilterOptions = 'all' | 'translated' | 'untranslated' | 'draft';
  type ILocaleOptions = 'draft' | 'diff';

  const { retailerLanguages } = useRetailer();

  const [isDiffView, toggleDiffView] = useState(false);
  const [tableData, setTableData] = useState<ILocaleTableDataRow[]>();
  const [searchText, setSearchText] = useState('');
  const [type, setFilterType] = useState<IFilterOptions>('all');
  const [draftTerms, setDraftTerms] = useState({});

  const [defaultTerms, setDefaultTerms] = useState({});
  const [publishedTerms, setPublishedTerms] = useState<Record<string, string>>({});
  const [diff, setDiff] = useState<readonly [DiffResult[], DiffResult[]]>();

  const [isPublishSnackbarOpen, togglePublishSnackbar] = useState(false);

  const [localeToggles, setLocaleToggles] = useState<ILocaleOptions[]>();

  const [contextGroupFilter, setContextGroupFilter] = useState<string>();
  const [contextPageFilter, setContextPageFilter] = useState<string>();
  const [contextComponentFilter, setContextComponentFilter] = useState<string>();
  const [contextDescriptionFilter, setContextDescriptionFilter] = useState<string>();

  const optionsLanguages = [
    <option key="" disabled>
      select locale
    </option>,
  ];

  retailerLanguages.forEach((lang, i) => {
    optionsLanguages.push(
      <option key={i} value={lang.code}>
        {lang.code} - {lang.language}
      </option>,
    );
  });

  // ! improve redundant LanguageSelector in the AppPanel
  const LanguageSelect = (
    <select name="language" value={locale} onChange={e => dispatch(setServiceLocale(e.target.value))}>
      {optionsLanguages}
    </select>
  );

  // ! Move search and filtering into table component
  useMemo(async () => {
    if (isLocaleEditMode && locale) {
      const publishedDefaultTerms = await LocaleUtils.getPublishedTerms(languages.ENGLISH);

      setDefaultTerms(publishedDefaultTerms);
      await LocaleUtils.getDraftTerms(locale, setDraftTerms);
      detectIsDraftTermsUpdated(false);
    }
  }, [isLocaleEditMode, isDraftTermsUpdated]);

  // ! Improve toggles
  useEffect(() => {
    const isDiffMode = localeToggles?.includes('diff') || false;
    const isEditMode = (locale !== languages.ENGLISH && localeToggles?.includes('draft')) || false;
    toggleDiffView(isDiffMode);
    dispatch(toggleLocaleEditMode(isEditMode));
  }, [localeToggles]);

  useEffect(() => {
    setTableData([]);
  }, [type]);

  const isLoadingData = Boolean(!Object.keys(draftTerms).length);

  useMemo(async () => {
    setPublishedTerms(await LocaleUtils.getPublishedTerms(locale));

    if (Object.keys(publishedTerms).length > 0) {
      // ? sort
      const rows: any = new Set();
      const contexts = Object.keys(defaultTerms);

      // ! Improve
      const searchFilter = (row: ILocaleTableDataRow) =>
        searchText.length
          ? row.context?.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()) ||
            row.defaultText?.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()) ||
            row.publishedText?.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()) ||
            row.draftText?.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase())
          : true;

      contexts.forEach(context => {
        const defaultText = defaultTerms[context];
        const publishedText = publishedTerms[context];
        const draftText = draftTerms[context];

        const isUnpublished = draftText?.length && draftText !== publishedText;
        const isTranslated = publishedText?.length && !isUnpublished;
        const isUntranslated = !publishedText?.length && !isUnpublished;
        const rowData = { context, defaultText, draftText, publishedText };

        if (contextGroupFilter && !context.split('.')[0].includes(contextGroupFilter)) return;
        if (contextPageFilter && !context.split('.')[1].includes(contextPageFilter)) return;
        if (contextComponentFilter && !context.split('.')[2].includes(contextComponentFilter)) return;
        if (contextDescriptionFilter && !context.split('.')[3].includes(contextDescriptionFilter)) return;

        if (type === 'translated' && isTranslated) {
          rows.add(rowData);
        } else if (type === 'untranslated' && isUntranslated) {
          rows.add(rowData);
        } else if (type === 'all') {
          rows.add(rowData);
        } else if (type === 'draft' && isUnpublished) {
          rows.add(rowData);
        }
      });

      const rowsArray: ILocaleTableDataRow[] = Array.from(rows);
      setTableData(rowsArray.filter(searchFilter));
      setDiff(LocaleUtils.generateDiff(publishedTerms, draftTerms));
    }
  }, [
    searchText,
    type,
    draftTerms,
    defaultTerms,
    contextGroupFilter,
    contextPageFilter,
    contextComponentFilter,
    contextDescriptionFilter,
  ]);

  const FilterSelect = (
    // @ts-ignore
    <select name="page" onChange={e => setFilterType(e.target.value)}>
      <option key={0} value="all">
        {`all (${Object.keys(defaultTerms).length})`}
      </option>
      <option key={1} value="draft">
        draft
      </option>
      <option key={2} value="translated">
        translated
      </option>
      <option key={3} value="untranslated">
        untranslated
      </option>
    </select>
  );

  const contextGroups = [];
  const contextPages = [];
  const contextComponents = [];
  const contextDescriptions = [];

  const optionsContextGroups = [<option key="" />];

  const optionsContextPages = [<option key="" />];

  const optionsContextComponents = [<option key="" />];

  const optionsContextDescriptions = [<option key="" />];

  Object.keys(defaultTerms).forEach(context => {
    if (!contextGroups.includes(context.split('.')[0])) contextGroups.push(context.split('.')[0]);
    if (!contextPages.includes(context.split('.')[1])) contextPages.push(context.split('.')[1]);
    if (!contextComponents.includes(context.split('.')[2])) contextComponents.push(context.split('.')[2]);
    if (!contextDescriptions.includes(context.split('.')[3])) contextDescriptions.push(context.split('.')[3]);
  });

  contextGroups.forEach((group, i) => {
    optionsContextGroups.push(
      <option key={i} value={group}>
        {group}
      </option>,
    );
  });

  contextPages.forEach((page, i) => {
    optionsContextPages.push(
      <option key={i} value={page}>
        {page}
      </option>,
    );
  });

  contextComponents.forEach((component, i) => {
    optionsContextComponents.push(
      <option key={i} value={component}>
        {component}
      </option>,
    );
  });

  contextDescriptions.forEach((description, i) => {
    optionsContextDescriptions.push(
      <option key={i} value={description}>
        {description}
      </option>,
    );
  });

  return (
    <>
      {/* ? Allow force RTL */}
      <div className="panel collapsible" role="presentation">
        Locale
      </div>

      <Box sx={{ margin: themeDevTools.spacing.medium }}>
        {isDevtoolsUserEditor && (
          <>
            <Box>
              <ToggleButtonGroup
                aria-label="locale-toggles"
                value={localeToggles}
                onChange={(_, options) => {
                  setLocaleToggles(options);
                }}
              >
                <StyledTooltip placement="bottom" title="Draft">
                  <ToggleButton
                    aria-label="draft"
                    value="draft"
                    sx={{
                      '&.Mui-selected, &.Mui-selected:hover': {
                        background: themeDevTools.color.orange,
                      },
                      '&:hover': {
                        background: themeDevTools.color.orange,
                      },
                    }}
                  >
                    <ModeEditIcon
                      sx={{
                        '&.Mui-selected, &.Mui-selected:hover': {
                          color: themeDevTools.color.black,
                        },
                        color: themeDevTools.color.white,
                        height: themeDevTools.spacing.large,
                        width: themeDevTools.spacing.large,
                      }}
                    />
                  </ToggleButton>
                </StyledTooltip>

                <StyledTooltip placement="bottom" title="View Diff">
                  <ToggleButton
                    aria-label="diff"
                    value="diff"
                    sx={{
                      '&.Mui-selected, &.Mui-selected:hover': {
                        background: themeDevTools.color.orange,
                      },
                      '&:hover': { background: themeDevTools.color.orange },
                    }}
                  >
                    <DifferenceIcon
                      sx={{
                        '&.Mui-selected, &.Mui-selected:hover': {
                          color: themeDevTools.color.black,
                        },
                        color: themeDevTools.color.white,
                        height: themeDevTools.spacing.large,
                        width: themeDevTools.spacing.large,
                      }}
                    />
                  </ToggleButton>
                </StyledTooltip>
              </ToggleButtonGroup>
            </Box>

            <div className="panel">
              <span className="title">Language</span>
              {LanguageSelect}
            </div>

            {isDiffView && <Viewer highlightInlineDiff lineNumbers diff={diff} indent={1} />}

            {/* // ! Add search box feature for looking up backend terms */}
            {isLocaleEditMode && (
              <>
                <div className="panel">
                  <Box sx={{ my: 2 }}>
                    <input placeholder="search" onChange={e => setSearchText(e.target.value)} />
                    {FilterSelect}
                  </Box>
                  {isApplicationPmi && (
                    <Box sx={{ my: 2 }}>
                      <DropdownSelect
                        options={optionsContextGroups}
                        title="Group"
                        onChange={e => setContextGroupFilter(e.target.value)}
                      />
                      <DropdownSelect
                        options={optionsContextPages}
                        title="Page"
                        onChange={e => setContextPageFilter(e.target.value)}
                      />
                      <DropdownSelect
                        options={optionsContextComponents}
                        title="Component"
                        onChange={e => setContextComponentFilter(e.target.value)}
                      />
                      <DropdownSelect
                        options={optionsContextDescriptions}
                        title="Description"
                        onChange={e => setContextDescriptionFilter(e.target.value)}
                      />
                    </Box>
                  )}

                  <Box sx={{ my: 1 }}>
                    <TableComponent
                      detectIsDraftTermsUpdated={detectIsDraftTermsUpdated}
                      isLoadingData={isLoadingData}
                      rowData={tableData}
                      rowType="LocaleRow"
                      tableHeadInfo={['context', languages.ENGLISH, locale]}
                    />
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button onClick={() => LocaleUtils.importTerms()}>Import</Button>
                    <Button onClick={() => LocaleUtils.exportTerms(tableData)}>Export</Button>
                    <Button
                      disabled={diff?.[0]?.length <= 2}
                      onClick={() => {
                        LocaleUtils.publishTerms();
                        // ! TODO: Invoke snackbar only upon successful publishing of terms
                        togglePublishSnackbar(true);
                      }}
                    >
                      Publish
                    </Button>
                  </Stack>

                  <SnackbarAlert
                    description=""
                    handleSnackbarOpen={togglePublishSnackbar}
                    isOpen={isPublishSnackbarOpen}
                    title="Published terms for current locale and invalidated distributions."
                  />
                </div>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};
